// İSG uzmanı portalı — 5x5 L-tipi matris seçici (Olasılık × Şiddet). Görsel ısı ızgarası:
// sütun = Olasılık (1-5), satır = Şiddet (5→1, yüksek üstte). Kullanıcı bir hücreye tıklar →
// (probability, severity) seçilir; altında **canlı skor + seviye rozeti** gösterilir.
//
// **⛔ SKOR/SEVİYE YALNIZ GÖSTERİM (§19 BAĞLAYICI).** Hücre rengi + rozet `deriveScore`/
// `deriveLevel` ile ANLIK hesaplanır (kullanıcı geri bildirimi); form/API'ye YALNIZ
// `probability`/`severity` gider — kalıcı `score`/`level` **sunucudan** gelir. İstemci burada
// hiçbir score/level GÖNDERMEZ.

import { cn } from '@isgportal/shared';
import { useTranslation } from 'react-i18next';
import { RiskLevelBadge } from './RiskLevelBadge';
import { RISK_FACTORS, deriveLevel, deriveScore } from './riskEnums';
import { RISK_LEVEL_CLASS } from './riskLevelStyles';

interface MatrixSelectProps {
  probability: number | null;
  severity: number | null;
  onChange: (probability: number, severity: number) => void;
  disabled?: boolean;
  /** Erişilebilirlik + benzersiz id öneki (aynı sayfada birden çok matris için). */
  idPrefix: string;
}

export function MatrixSelect({
  probability,
  severity,
  onChange,
  disabled,
  idPrefix,
}: MatrixSelectProps) {
  const { t } = useTranslation();
  const previewScore = deriveScore(probability, severity);
  const previewLevel = deriveLevel(previewScore);

  // Şiddet satırları yukarıdan aşağıya 5→1 (yüksek şiddet üstte — konvansiyonel matris).
  const severityRows = [...RISK_FACTORS].reverse();

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-stretch gap-2">
        {/* Şiddet ekseni etiketi (dikey) */}
        <div className="flex items-center">
          <span className="text-[10px] font-semibold text-muted [writing-mode:vertical-rl] [transform:rotate(180deg)]">
            {t('risk.matrix.severity')}
          </span>
        </div>
        <div className="flex flex-col gap-1">
          {/* Izgara zaten RiskItemScoreFields'ta bir fieldset+legend içinde gruplanır; her hücre
              tam aria-label taşır. Ek ARIA rol vermek biome useSemanticElements'i tetikler. */}
          <div
            className="grid gap-1"
            aria-label={t('risk.matrix.gridLabel')}
            style={{ gridTemplateColumns: `repeat(${RISK_FACTORS.length}, minmax(2.25rem, 1fr))` }}
          >
            {severityRows.map((sev) =>
              RISK_FACTORS.map((prob) => {
                const cellScore = prob * sev;
                const cellLevel = deriveLevel(cellScore);
                const selected = probability === prob && severity === sev;
                return (
                  <button
                    key={`${prob}-${sev}`}
                    type="button"
                    id={`${idPrefix}-cell-${prob}-${sev}`}
                    disabled={disabled}
                    aria-pressed={selected}
                    aria-label={t('risk.matrix.cellLabel', {
                      probability: prob,
                      severity: sev,
                      score: cellScore,
                      level: cellLevel ? t(`risk.level.${cellLevel}`) : '',
                    })}
                    onClick={() => onChange(prob, sev)}
                    className={cn(
                      'flex h-9 items-center justify-center rounded-md text-xs font-semibold tabular-nums transition-all',
                      cellLevel ? RISK_LEVEL_CLASS[cellLevel] : 'bg-surface-muted text-muted',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      'disabled:cursor-not-allowed disabled:opacity-40',
                      // Owner review B1 item 2 — eskiden HER hücre `opacity-45` ile başlıyordu
                      // (seçili olan `hover:opacity-90`e çıkıyordu): bu, dolu zeminle sabit
                      // metnin AA çiftini sayfa arkaplanına doğru SOLDURUYORdu (sarı zemin +
                      // grafit metin arkaplanla karışınca gri bir metne düşüyor, "gray numbers
                      // on yellow" bulgusu — ısı skalasının TAMAMI "washed-out" görünüyordu).
                      // Artık dinlenme hâlinde TÜM hücreler tam renk (token'ın AA'sı KORUNUR);
                      // seçili olmayan hücrede etkileşim sinyali bir halka (opaklık DEĞİL).
                      selected
                        ? 'ring-2 ring-ring ring-offset-1 ring-offset-surface'
                        : 'hover:ring-2 hover:ring-inset hover:ring-fg/30',
                    )}
                  >
                    {cellScore}
                  </button>
                );
              }),
            )}
          </div>
          {/* Olasılık ekseni etiketi (yatay) */}
          <span className="text-center text-[10px] font-semibold text-muted">
            {t('risk.matrix.probability')}
          </span>
        </div>
      </div>

      {/* Canlı önizleme (yalnız gösterim) */}
      <div className="flex items-center gap-2 text-xs text-muted" aria-live="polite">
        {previewLevel && previewScore != null ? (
          <>
            <span>{t('risk.matrix.previewLabel')}</span>
            <RiskLevelBadge level={previewLevel} score={previewScore} />
          </>
        ) : (
          <span>{t('risk.matrix.previewEmpty')}</span>
        )}
      </div>
    </div>
  );
}
